import React, { useEffect, useState } from 'react';
import '../styles/AuditLogs.css';
import Service from '../services/Service';
import PatientService from '../../PatientList/services/Service';
import { useLocation, useSearchParams } from 'react-router-dom';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');

  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchAuditLogs(page);
  }, [page, selectedPatientId]);

  useEffect(() => {
    const loadPatients = async () => {
      try {
        const res = await PatientService.getPatientList({ page: 1 });
        const list = res?.data?.results?.results || [];
        setPatients(list);
      } catch (e) {
        console.error('Error fetching patients list:', e);
      }
    };
    loadPatients();
  }, []);

  useEffect(() => {
    const qpId = searchParams.get('patient_id');
    const stateId = location?.state?.selectedPatientId;
    const initialId = qpId || stateId;
    if (initialId) {
      setSelectedPatientId(String(initialId));
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAuditLogs = async (pageNumber = 1, searchValue = searchTerm) => {
    try {
      setLoading(true);

      // Pass pagination + search params
      const params = {
        page: pageNumber,
        page_size: pageSize,
        patient_name: searchValue,
        patient_id: selectedPatientId || undefined,
      };

      if (searchValue) {
        params.patient_name = searchValue; // 🔍 assuming backend supports `search` param
      }

      const response = await Service.getAuditLogsList(params);

      if (response?.data?.results) {
        setLogs(response.data.results);
        setTotalCount(response.data.count);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1); // reset to first page when new search
    fetchAuditLogs(1, searchTerm);
  };

  const handlePatientChange = (e) => {
    const value = e.target.value;
    setSelectedPatientId(value);
    setPage(1);
    fetchAuditLogs(1);
  };

  const handleNext = () => {
    if (page * pageSize < totalCount) {
      setPage(page + 1);
    }
  };

  const handlePrevious = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  return (
    <div className="audit-logs-container">
      <h2>Audit Logs</h2>

      {/* 🔍 Search Bar */}
      <div className="search-container">
        <select value={selectedPatientId} onChange={handlePatientChange} className="search-input">
          <option value="">All patients</option>
          {patients.map((p) => (
            <option key={p.id} value={p.id}>
              {`${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search by patient name or operation..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <button className="search-btn" onClick={handleSearch}>
          Search
        </button>
      </div>

      {loading ? (
        <p className="loading">Loading...</p>
      ) : (
        <table className="audit-logs-table">
          <thead>
            <tr>
              <th>Sr No</th>
              <th>Patient Name</th>
              <th>Operation</th>
              <th>Occurred At (EST) </th>
            </tr>
          </thead>
          <tbody>
            {logs.length > 0 ? (
              logs.map((log, index) => (
                <tr key={log.id}>
                  <td>{(page - 1) * pageSize + index + 1}</td>
                  <td>{log.patient_name || '-'}</td>
                  <td>{log.operation}</td>
                  <td>{new Date(log.occurred_at).toLocaleString('en-US', { timeZone: 'America/New_York' })}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="no-data">
                  No audit logs found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Pagination Controls */}
      <div className="pagination-controls">
        <button className="pagination-btn" onClick={handlePrevious} disabled={page === 1}>
          ← Previous
        </button>
        <span className="pagination-info">
          Page {page} of {Math.ceil(totalCount / pageSize) || 1}
        </span>
        <button className="pagination-btn" onClick={handleNext} disabled={page * pageSize >= totalCount}>
          Next →
        </button>
      </div>
    </div>
  );
};

export default AuditLogs;
